import { defineStore } from 'pinia';
import { ref } from 'vue';

export const useMarketingBuilderStore = defineStore('marketing/builder', () => {
  const emailType = ref<string | null>(null);
  const enabled = ref<boolean | null>(null);
  const showToggle = ref<boolean>(false);
  const title = ref<string>('');
  const description = ref<string>('');
  const showFilters = ref<boolean>(false);
  const sentDescription = ref<string>('');
  const receiveDescription = ref<string>('');
  const filteredCustomersCount = ref<number | null>(null);
  const loadingCustomersCount = ref<boolean>(false);
  const showRebookSettings = ref<boolean>(false);
  const showReset = ref<boolean>(false);
  const showSendTest = ref<boolean>(false);
  const showSchedule = ref<boolean>(false);
  const hideDiscount = ref<boolean>(false);
  const hideButtonToggle = ref<boolean>(false);
  const hideButtonOptions = ref<boolean>(false);
  const selectedView = ref<'DESKTOP' | 'MOBILE'>('DESKTOP');
  const allowImageUpload = ref<boolean>(false);

  const setEnabled = (value: boolean) => {
    enabled.value = value;
  };

  const setFilteredCustomersCount = (value: number) => {
    filteredCustomersCount.value = value;
  };

  const setLoadingCustomersCount = (value: boolean) => {
    loadingCustomersCount.value = value;
  };

  const setSelectedView = (value: 'DESKTOP' | 'MOBILE') => {
    selectedView.value = value;
  };

  const resetState = () => {
    emailType.value = null;
    enabled.value = null;
    showToggle.value = false;
    title.value = '';
    description.value = '';
    showFilters.value = false;
    sentDescription.value = '';
    receiveDescription.value = '';
    filteredCustomersCount.value = null;
    loadingCustomersCount.value = false;
    showRebookSettings.value = false;
    showReset.value = false;
    showSendTest.value = false;
    showSchedule.value = false;
    hideDiscount.value = false;
    hideButtonToggle.value = false;
    hideButtonOptions.value = false;
    selectedView.value = 'DESKTOP';
    allowImageUpload.value = false;
  };

  const setSettings = (payload: {
    emailType?: string;
    title?: string;
    description?: string;
    sentDescription?: string;
    receiveDescription?: string;
    showToggle?: boolean;
    showSendTest?: boolean;
    showSchedule?: boolean;
    showFilters?: boolean;
    showRebookSettings?: boolean;
    showReset?: boolean;
    hideDiscount?: boolean;
    hideButtonToggle?: boolean;
    hideButtonOptions?: boolean;
    allowImageUpload?: boolean;
  }) => {
    const alwaysSet: Array<keyof typeof payload> = [
      'emailType',
      'title',
      'description',
      'sentDescription',
      'receiveDescription',
    ];

    alwaysSet.forEach((key) => {
      if (payload[key] !== undefined) {
        const refKey = key as keyof typeof payload;
        const refs: Record<string, typeof emailType> = {
          emailType,
          title,
          description,
          sentDescription,
          receiveDescription,
        };
        refs[refKey].value = payload[key] as any;
      }
    });

    const onlySetWhenTrue: Array<keyof typeof payload> = [
      'showRebookSettings',
      'showSendTest',
      'showToggle',
      'showFilters',
      'allowImageUpload',
      'showSchedule',
      'showReset',
      'hideDiscount',
      'hideButtonOptions',
      'hideButtonToggle',
    ];

    onlySetWhenTrue.forEach((key) => {
      if (payload[key]) {
        const refKey = key as keyof typeof payload;
        const refs: Record<string, typeof showRebookSettings> = {
          showRebookSettings,
          showSendTest,
          showToggle,
          showFilters,
          allowImageUpload,
          showSchedule,
          showReset,
          hideDiscount,
          hideButtonOptions,
          hideButtonToggle,
        };
        refs[refKey].value = payload[key] as any;
      }
    });
  };

  return {
    emailType,
    enabled,
    showToggle,
    title,
    description,
    showFilters,
    sentDescription,
    receiveDescription,
    filteredCustomersCount,
    loadingCustomersCount,
    showRebookSettings,
    showReset,
    showSendTest,
    showSchedule,
    hideDiscount,
    hideButtonToggle,
    hideButtonOptions,
    selectedView,
    allowImageUpload,
    setEnabled,
    setFilteredCustomersCount,
    setLoadingCustomersCount,
    setSelectedView,
    resetState,
    setSettings,
  };
});
